/**
 * Business Failure Warning - Client Detail Page
 *
 * Opens from the failure-warning table. Instead of jumping to the client
 * profile, this page interprets the risk indicators and provides an
 * accountant-ready action plan:
 *   - risk score methodology (how it's calculated)
 *   - per-indicator breakdown with value, score, weight, contribution
 *   - indicator-specific mitigation recommendations
 *   - step-by-step plan by risk level
 *   - internal links to relevant RoniDoc tools
 *   - authoritative ATO / ASIC / legislation citations
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  AlertOctagon, ArrowLeft, AlertTriangle, CheckCircle2, Shield, ExternalLink,
  ClipboardList, Scale, FileCheck, BarChart3, Calculator, BookOpen, Phone, Users,
  TrendingDown, CreditCard, Building2, Zap, Info, Target,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string {
  if (v == null || isNaN(v)) return '-';
  return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`;
}

const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
  itemStyle: { color: '#94a3b8' },
  wrapperStyle: { outline: 'none' as const },
  cursor: false,
};

const RISK_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  moderate: '#f59e0b',
  medium: '#f59e0b', // API returns 'medium', frontend sometimes uses 'moderate'
  low: '#22c55e',
};

// Map raw indicator score (0-100, higher=better) to a severity bucket
function severityFromScore(score: number): 'critical' | 'warning' | 'watch' | 'healthy' {
  if (score < 30) return 'critical';
  if (score < 50) return 'warning';
  if (score < 70) return 'watch';
  return 'healthy';
}

const SEVERITY_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30', label: 'Critical' },
  warning: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30', label: 'Warning' },
  watch: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/30', label: 'Watch' },
  healthy: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/30', label: 'Healthy' },
};

// ==================== Indicator metadata ====================
// Each of the 6 scoring dimensions has a formula, thresholds, and mitigation advice

interface IndicatorMeta {
  formula: string;
  thresholds: { score: number; range: string }[];
  whyItMatters: string;
  mitigations: string[];
}

const INDICATOR_META: Record<string, IndicatorMeta> = {
  'Profitability': {
    formula: 'Net Profit ÷ Total Revenue × 100',
    thresholds: [
      { score: 100, range: 'Margin > 10% - strong' },
      { score: 70,  range: 'Margin 5 - 10% - acceptable' },
      { score: 40,  range: 'Margin 0 - 5% - marginal' },
      { score: 10,  range: 'Margin < 0% - loss-making' },
    ],
    whyItMatters: 'Profit margin is the single clearest signal of business viability. A loss-making business burns cash that must be replaced from equity, debt, or asset sales - all of which compound risk over time.',
    mitigations: [
      'Review pricing: benchmark against competitors and consider a structured price increase (even 3-5% flows directly to the bottom line).',
      'Audit direct costs: supplier consolidation, volume discounts, substitution of cheaper inputs.',
      'Cut discretionary expenses: travel, subscriptions, consulting engagements with unclear ROI.',
      'Identify loss-making product lines or customers and consider exiting or repricing them.',
      'Review wage-to-revenue ratio vs ATO industry benchmarks - over-staffing is often the largest controllable cost.',
    ],
  },
  'Revenue Trend': {
    formula: '(Current Year Revenue − Prior Year Revenue) ÷ Prior Year Revenue × 100',
    thresholds: [
      { score: 100, range: 'Growth > 10% - expanding' },
      { score: 70,  range: 'Growth 0 - 10% - stable' },
      { score: 40,  range: 'Decline 0 - 10% - shrinking' },
      { score: 10,  range: 'Decline > 10% - severe contraction' },
    ],
    whyItMatters: 'Revenue decline is often the earliest lagging indicator of business failure - customer attrition, product obsolescence, or market contraction show up here before profit collapse.',
    mitigations: [
      'Segment revenue by customer, product, and channel to identify where the decline is concentrated.',
      'Retention campaign for top 20% of customers - it is 5× cheaper to retain than acquire.',
      'Review sales pipeline health: lead volume, conversion rate, deal velocity.',
      'Investigate competitor activity and pricing changes in the segment.',
      'Consider diversification into adjacent products or channels to offset core decline.',
    ],
  },
  'Liquidity': {
    formula: 'Current Assets ÷ Current Liabilities (Current Ratio)',
    thresholds: [
      { score: 100, range: 'Ratio > 2.0 - very liquid' },
      { score: 80,  range: 'Ratio 1.5 - 2.0 - comfortable' },
      { score: 50,  range: 'Ratio 1.0 - 1.5 - tight' },
      { score: 15,  range: 'Ratio < 1.0 - can\'t cover short-term debts' },
    ],
    whyItMatters: 'A current ratio below 1 means the business cannot pay its short-term obligations if they all fell due today. Combined with declining revenue, this is the classic pre-insolvency signature.',
    mitigations: [
      'Accelerate debtor collection: shorten payment terms, automated chasing, early-payment discounts.',
      'Negotiate extended supplier terms (30 → 45 days buys time without borrowing).',
      'Factor or invoice-finance receivables if cash is needed within days.',
      'Defer non-critical capex and subscription renewals.',
      'Review inventory: excess stock locks up cash with no return.',
    ],
  },
  'Debt Ratio': {
    formula: 'Total Liabilities ÷ Total Assets × 100',
    thresholds: [
      { score: 100, range: 'Under 30% - conservative' },
      { score: 70,  range: '30 - 50% - moderate leverage' },
      { score: 40,  range: '50 - 70% - high leverage' },
      { score: 10,  range: 'Over 70% - very high leverage' },
    ],
    whyItMatters: 'High leverage magnifies both gains and losses. In a downturn, highly-leveraged businesses have no headroom - covenant breaches lead to facility withdrawal which often triggers insolvency.',
    mitigations: [
      'Prioritise paying down the highest-cost debt first (credit cards > overdraft > term loans).',
      'Refinance short-term debt into longer tenor to reduce principal repayment pressure.',
      'Consider equity injection or retention of profits rather than dividends to strengthen balance sheet.',
      'Monitor covenants monthly, especially interest coverage and leverage ratios - proactively engage lender before breach.',
      'Sell non-core assets (surplus equipment, investment property) to reduce debt.',
    ],
  },
  'Expense Control': {
    formula: 'Total Operating Expenses ÷ Total Revenue × 100',
    thresholds: [
      { score: 100, range: 'Under 70% - efficient' },
      { score: 70,  range: '70 - 85% - reasonable' },
      { score: 40,  range: '85 - 95% - thin margins' },
      { score: 10,  range: 'Over 95% - unsustainable' },
    ],
    whyItMatters: 'When operating expenses consume nearly all revenue, any small shock - a lost customer, price increase, bad debt - tips the business into loss. Low margin = low resilience.',
    mitigations: [
      'Zero-base budget exercise: justify every line item from scratch rather than inflating last year.',
      'Benchmark expense categories against ATO industry benchmarks to spot outliers.',
      'Renegotiate or tender major supplier contracts (utilities, insurance, software).',
      'Evaluate fixed vs variable cost mix - convert fixed to variable where possible (outsourcing, lease vs buy).',
      'Identify lowest-margin products/services and either reprice or discontinue.',
    ],
  },
  'Working Capital': {
    formula: '(Current Assets − Current Liabilities) ÷ Revenue × 100',
    thresholds: [
      { score: 100, range: 'Over 20% of revenue - strong buffer' },
      { score: 70,  range: '10 - 20% - adequate' },
      { score: 40,  range: '0 - 10% - thin' },
      { score: 10,  range: 'Negative - operating on borrowed time' },
    ],
    whyItMatters: 'Working capital is the operational cash cushion. Negative working capital means the business is funded by creditors - one demand for early payment or a single large invoice uncollected can cause a failure cascade.',
    mitigations: [
      'Reduce debtor days: weekly chasing, automatic reminders, stop-credit rules for repeat late payers.',
      'Optimise stock: just-in-time ordering, clearance of slow-moving lines.',
      'Extend creditor days within tolerance (without harming supplier relationships).',
      'Establish or increase a working capital facility (overdraft, invoice finance) as a buffer.',
      'Consider an equity or director loan injection to shore up the balance sheet.',
    ],
  },
};

// ==================== Action plans by risk level ====================

interface ActionStep {
  title: string;
  description: string;
  icon: any;
  urgency: 'immediate' | 'this-week' | 'this-month' | 'ongoing';
}

const ACTION_PLANS: Record<string, ActionStep[]> = {
  critical: [
    { urgency: 'immediate', icon: Phone, title: 'Engage insolvency advisor within 48 hours',
      description: 'Directors must seek immediate advice from a Registered Liquidator or insolvency lawyer. Continuing to trade while insolvent exposes directors to personal liability under s588G of the Corporations Act 2001.' },
    { urgency: 'immediate', icon: AlertOctagon, title: 'Cease incurring new debts',
      description: 'Do not enter new credit arrangements, sign contracts, or take deposits for work you may not deliver. Freeze discretionary spending and stop dividend distributions.' },
    { urgency: 'immediate', icon: Shield, title: 'Safe harbour assessment (s588GA)',
      description: 'If directors are developing a reasonable course of action likely to lead to better outcomes than immediate liquidation, document it and maintain employee entitlements and tax obligations to access Safe Harbour protection.' },
    { urgency: 'this-week', icon: FileCheck, title: 'Lodge outstanding BAS and super',
      description: 'Directors are personally liable for unpaid PAYG withholding, GST and SGC under Director Penalty Notices (DPN). Lodging within 3 months preserves the ability to avoid personal liability by putting the company into administration.' },
    { urgency: 'this-week', icon: ClipboardList, title: 'Consider Small Business Restructuring (SBR)',
      description: 'Eligible if liabilities under $1M. Allows company to propose a debt restructuring plan while directors retain control. Must appoint a Small Business Restructuring Practitioner.' },
    { urgency: 'this-week', icon: Users, title: 'Notify key stakeholders (privileged)',
      description: 'Discuss with major secured creditors, landlord and bank under without-prejudice/legal privilege. Do NOT make unauthorised preferential payments to related parties - these are voidable transactions under s588FA.' },
    { urgency: 'this-month', icon: BarChart3, title: 'Prepare a 13-week cash flow forecast',
      description: 'Weekly granularity required for restructuring plan. Identify minimum cash runway and breakeven point.' },
  ],
  high: [
    { urgency: 'this-week', icon: BarChart3, title: 'Run a 13-week cash flow forecast',
      description: 'Project weekly inflows and outflows, identify cash gap. Use RoniDoc Cash Flow Forecast + export to share with lender.' },
    { urgency: 'this-week', icon: CreditCard, title: 'Review debt & covenant position',
      description: 'List all facilities, repayment dates, covenants. Contact lender early if covenants at risk - banks strongly prefer proactive engagement to surprise defaults.' },
    { urgency: 'this-week', icon: FileCheck, title: 'Audit ATO compliance',
      description: 'Ensure BAS, IAS, PAYGW, SGC all lodged and up to date. If unable to pay, apply for an ATO payment arrangement (interactive ATO Business Portal).' },
    { urgency: 'this-month', icon: TrendingDown, title: 'Cost base reduction plan',
      description: 'Benchmark against RoniDoc ATO industry benchmarks. Identify discretionary costs, supplier renegotiation opportunities, and headcount risk.' },
    { urgency: 'this-month', icon: Scale, title: 'Working capital optimisation',
      description: 'Tighten debtor days (invoice promptly, implement 7-day terms for new customers), negotiate supplier terms, review stock holding.' },
    { urgency: 'this-month', icon: Zap, title: 'Document director\'s diligence trail',
      description: 'Board minutes capturing solvency assessment, professional advice sought, financial covenants reviewed. Essential if ASIC later investigates.' },
    { urgency: 'ongoing', icon: CheckCircle2, title: 'Monthly board solvency review',
      description: 'Document review of net current asset position, cash coverage ratio, and forecast vs actual cash flow.' },
  ],
  moderate: [
    { urgency: 'this-month', icon: BarChart3, title: 'Quarterly cash flow forecast',
      description: 'Roll a 12-month cash flow model, stress-test against a 10% revenue decline and a 30-day debtor stretch.' },
    { urgency: 'this-month', icon: Shield, title: 'Tax planning review',
      description: 'Use RoniDoc Tax Planning module to identify deduction timing, instant asset write-off eligibility, and R&D tax offset. Maximise franking account utilisation.' },
    { urgency: 'this-month', icon: Calculator, title: 'Working capital benchmark',
      description: 'Compare debtor days, creditor days and stock turn against ATO industry benchmarks. A 5-day reduction in debtor days can release significant cash.' },
    { urgency: 'ongoing', icon: CheckCircle2, title: 'Monthly financial review',
      description: 'Schedule monthly management accounts review with the client to spot trends early.' },
  ],
  low: [
    { urgency: 'this-month', icon: CheckCircle2, title: 'Continue proactive monitoring',
      description: 'Risk indicators are low. Maintain quarterly management account reviews and annual strategic planning.' },
    { urgency: 'this-month', icon: TrendingDown, title: 'Opportunity scan',
      description: 'Use RoniDoc Opportunity Scanner to identify growth or tax-saving opportunities while the business is healthy.' },
  ],
};

// API may return 'medium' - map to 'moderate' plan
ACTION_PLANS.medium = ACTION_PLANS.moderate;

// ==================== Regulatory / ATO references ====================

interface Reference {
  title: string;
  source: string;
  url: string;
  description: string;
  icon: any;
  appliesTo: string[];
}

const REFERENCES: Reference[] = [
  {
    title: 'Director Penalty Notices (DPN)',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/corporate-tax-measures-and-assurance/in-detail/director-penalty-notices',
    description: 'Directors are personally liable for unpaid PAYG withholding, GST and SGC unless the company is placed into administration or liquidation within 21 days of the DPN being issued. For amounts unreported after 3 months, personal liability is automatic (lockdown DPN).',
    icon: AlertOctagon,
    appliesTo: ['critical', 'high', 'moderate', 'medium'],
  },
  {
    title: 'Insolvent Trading - What to do if your company is insolvent',
    source: 'ASIC / Corporations Act 2001 s588G',
    url: 'https://www.asic.gov.au/regulatory-resources/insolvency/insolvency-for-directors/what-to-do-if-your-company-is-insolvent/',
    description: 'A director may be personally liable for debts incurred by the company while insolvent. Penalties include civil compensation, pecuniary penalties, and criminal charges. Directors must act as soon as there are reasonable grounds to suspect insolvency.',
    icon: Scale,
    appliesTo: ['critical', 'high'],
  },
  {
    title: 'RG 217 - Duty to Prevent Insolvent Trading (Safe Harbour)',
    source: 'ASIC Regulatory Guide / Corporations Act s588GA',
    url: 'https://www.asic.gov.au/regulatory-resources/find-a-document/regulatory-guides/rg-217-duty-to-prevent-insolvent-trading-guide-for-directors/',
    description: 'Official ASIC guidance on directors\' duties under s588G and the safe harbour protection under s588GA. Protects directors from insolvent trading liability if they develop a course of action reasonably likely to lead to a better outcome than immediate administration/liquidation. Requires up-to-date books, employee entitlements paid, and tax lodgements current.',
    icon: Shield,
    appliesTo: ['critical', 'high'],
  },
  {
    title: 'Small Business Restructuring (SBR) & Restructuring Plan',
    source: 'ASIC',
    url: 'https://www.asic.gov.au/regulatory-resources/insolvency/insolvency-for-directors/small-business-restructuring-and-the-restructuring-plan/',
    description: 'Debt restructuring path for incorporated businesses with liabilities under $1 million. Directors retain control while a Small Business Restructuring Practitioner facilitates a plan with creditors. 20 business days to propose plan; creditors vote within 15 days.',
    icon: ClipboardList,
    appliesTo: ['critical', 'high'],
  },
  {
    title: 'ATO Payment Plans',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/paying-the-ato/help-with-paying',
    description: 'If unable to pay on time, businesses can apply for a payment plan. Arrangements under $200,000 can often be set up online via the Business Portal. The ATO charges General Interest Charge (GIC), currently ~11% p.a., compounding daily.',
    icon: CreditCard,
    appliesTo: ['critical', 'high', 'moderate', 'medium'],
  },
  {
    title: 'Voluntary Administration - Guide for Creditors',
    source: 'ASIC',
    url: 'https://www.asic.gov.au/regulatory-resources/insolvency/insolvency-for-creditors/voluntary-administration-a-guide-for-creditors/',
    description: 'Directors appoint an external administrator to take control and investigate options: Deed of Company Arrangement (DOCA), return to directors, or liquidation. Creditors vote at second meeting (~25 business days after appointment).',
    icon: Building2,
    appliesTo: ['critical'],
  },
  {
    title: 'ATO Small Business Benchmarks',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/small-business-benchmarks',
    description: 'Industry benchmarks for gross profit, labour, rent and motor vehicle costs as a ratio of turnover. Deviations can indicate under-reporting, operational inefficiency, or inform stress-testing of the business model.',
    icon: BarChart3,
    appliesTo: ['critical', 'high', 'moderate', 'medium', 'low'],
  },
  {
    title: 'Superannuation Guarantee Charge (SGC)',
    source: 'ATO',
    url: 'https://www.ato.gov.au/businesses-and-organisations/super-for-employers/missed-and-late-super-guarantee-payments',
    description: 'Unpaid super attracts SGC = super shortfall + 10% p.a. interest + $20 per employee per quarter administration fee. SGC is NOT tax deductible. Directors are personally liable under DPN rules (lockdown DPN after 3 months).',
    icon: Users,
    appliesTo: ['critical', 'high', 'moderate', 'medium'],
  },
  {
    title: 'Insolvency Information for Directors, Employees, Creditors & Shareholders',
    source: 'ASIC / Corporations Act 2001 s588FA',
    url: 'https://www.asic.gov.au/regulatory-resources/insolvency/insolvency-information-for-directors-employees-creditors-and-shareholders/',
    description: 'Gateway to ASIC\'s full insolvency information sheet library. Covers preference payments and voidable transactions (s588FA): payments made to creditors within 6 months of insolvency that give them a preference may be clawed back by a liquidator. Related-party payments within 4 years are subject to heightened scrutiny.',
    icon: AlertTriangle,
    appliesTo: ['critical', 'high'],
  },
];

// ==================== Internal RoniDoc tool recommendations ====================

interface InternalTool {
  title: string;
  description: string;
  icon: any;
  href: string;
  appliesTo: string[];
}

const INTERNAL_TOOLS: InternalTool[] = [
  { title: 'Cash Flow Forecast', description: 'Build a 13-week weekly forecast from Xero data',
    icon: BarChart3, href: '/panel/compliance/cashflow', appliesTo: ['critical', 'high', 'moderate', 'medium'] },
  { title: 'Smart Comparison', description: 'Year-over-year trend analysis to quantify deterioration',
    icon: TrendingDown, href: '/panel/intelligence/comparison', appliesTo: ['critical', 'high', 'moderate', 'medium'] },
  { title: 'Reconciliation', description: 'Trial balance vs P&L vs Balance Sheet integrity check',
    icon: Scale, href: '/panel/intelligence/reconciliation', appliesTo: ['critical', 'high'] },
  { title: 'Compliance Checker', description: 'Benchmark margins and ratios vs ATO industry data',
    icon: Shield, href: '/panel/compliance/checker', appliesTo: ['critical', 'high', 'moderate', 'medium', 'low'] },
  { title: 'BAS Validator', description: 'Verify current BAS period accuracy before lodgement',
    icon: FileCheck, href: '/panel/compliance/bas', appliesTo: ['critical', 'high', 'moderate', 'medium'] },
  { title: 'Tax Planning', description: 'Identify deduction timing and year-end strategies',
    icon: Calculator, href: '/panel/compliance/tax-planning', appliesTo: ['moderate', 'medium', 'low'] },
  { title: 'Opportunity Scanner', description: 'Find tax-saving and growth opportunities',
    icon: Zap, href: '/panel/compliance/opportunities', appliesTo: ['moderate', 'medium', 'low'] },
];

const URGENCY_LABELS: Record<string, { label: string; color: string }> = {
  immediate: { label: 'Within 48 hours', color: 'text-red-600 bg-red-500/10' },
  'this-week': { label: 'This week', color: 'text-orange-600 bg-orange-500/10' },
  'this-month': { label: 'This month', color: 'text-blue-600 bg-blue-500/10' },
  ongoing: { label: 'Ongoing', color: 'text-green-600 bg-green-500/10' },
};

// ==================== Component ====================

export default function FailureWarningDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const preloaded = (location.state as any)?.client;

  const { data: scanData, isLoading } = useQuery({
    queryKey: ['failure-warning'],
    queryFn: async () => { const r = await apiClient.get('/failure-warning/scan'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const client = scanData?.clients?.find((c: any) => c.clientId === clientId) || preloaded;

  if (isLoading && !preloaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-sm text-muted-foreground">Loading risk assessment...</span>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/intelligence/failure-warning')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Risk Warning
        </Button>
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
            <p>Client not found in the current risk scan.</p>
            <p className="text-xs mt-2">It may have been removed or the scan may need to be re-run.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const riskLevel = client.riskLevel || 'low';
  const riskLevelKey = riskLevel === 'medium' ? 'moderate' : riskLevel; // UI display
  const riskColor = RISK_COLORS[riskLevel] || RISK_COLORS.low;
  const healthScore = client.healthScore ?? (100 - (client.riskScore || 0));
  const actionPlan = ACTION_PLANS[riskLevel] || ACTION_PLANS.low;
  const relevantRefs = REFERENCES.filter(r => r.appliesTo.includes(riskLevel));
  const relevantTools = INTERNAL_TOOLS.filter(t => t.appliesTo.includes(riskLevel));

  // Indicator breakdown with contribution calculation
  // contribution to health = score × weight (out of 100)
  // contribution to risk = (100 - score) × weight (lost points)
  const indicators = (client.indicators || []) as Array<{ name: string; value: number; score: number; weight: number; detail: string }>;
  const breakdown = indicators.map(ind => ({
    name: ind.name,
    value: ind.value,
    score: ind.score,
    weight: ind.weight,
    weightPct: Math.round(ind.weight * 100),
    detail: ind.detail,
    healthContribution: Math.round(ind.score * ind.weight * 10) / 10,
    riskContribution: Math.round((100 - ind.score) * ind.weight * 10) / 10,
    severity: severityFromScore(ind.score),
    meta: INDICATOR_META[ind.name],
  }));
  // Sort by risk contribution descending (biggest problems first)
  breakdown.sort((a, b) => b.riskContribution - a.riskContribution);

  const chartData = breakdown.map(b => ({
    name: b.name,
    'Health points': b.healthContribution,
    'Risk points (lost)': b.riskContribution,
    weight: b.weightPct,
  }));

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/panel/intelligence/failure-warning')} className="gap-2">
        <ArrowLeft className="h-4 w-4" /> Back to Risk Warning
      </Button>

      {/* Header */}
      <PageHeader
        icon={AlertOctagon}
        title={`${client.clientName} - Risk Action Plan`}
        description="How the risk score is calculated, what each indicator means, and how to mitigate it"
        iconColor={riskLevel === 'critical' ? 'bg-red-500/10 text-red-500' : riskLevel === 'high' ? 'bg-orange-500/10 text-orange-500' : (riskLevel === 'moderate' || riskLevel === 'medium') ? 'bg-yellow-500/10 text-yellow-500' : 'bg-green-500/10 text-green-500'}
      />

      {/* Risk Score Banner */}
      <Card className="border-l-4" style={{ borderLeftColor: riskColor }}>
        <CardContent className="pt-5 pb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: riskColor }}>
                {client.riskScore}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h2 className="text-lg font-semibold">{client.clientName}</h2>
                  <Badge variant="outline" className="text-[10px] capitalize">{client.entityType}</Badge>
                  <Badge style={{ backgroundColor: riskColor + '20', color: riskColor, border: 'none' }} className="text-[10px] uppercase font-semibold">
                    {riskLevelKey} risk
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Risk score <span className="font-semibold">{client.riskScore}/100</span> (health score {healthScore}/100) · {indicators.length} indicators · FY {client.financialYear}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6 text-right">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Revenue</p>
                <p className="text-base font-semibold">{fmtCurrency(client.currentRevenue)}</p>
                <p className={cn('text-[10px]', client.revenueChange >= 0 ? 'text-green-600' : 'text-red-600')}>
                  {client.revenueChange >= 0 ? '+' : ''}{client.revenueChange}% YoY
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Profit</p>
                <p className={cn('text-base font-semibold', client.currentProfit >= 0 ? '' : 'text-red-600')}>{fmtCurrency(client.currentProfit)}</p>
                <p className="text-[10px] text-muted-foreground">{client.profitMargin}% margin</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Indicators</p>
                <p className="text-base font-semibold">{indicators.length}</p>
                <p className="text-[10px] text-muted-foreground">{breakdown.filter(b => b.severity === 'critical').length} critical</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How is the risk score calculated? */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            How is this risk score calculated?
          </CardTitle>
          <CardDescription className="text-xs">
            The score is a weighted average of six financial health dimensions sourced from Xero P&L and Balance Sheet data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-md bg-muted/40 font-mono text-xs leading-relaxed">
            <div><span className="text-muted-foreground">Step 1:</span> Each indicator receives a score 0-100 based on its ratio value vs industry thresholds.</div>
            <div><span className="text-muted-foreground">Step 2:</span> Health Score = Σ (indicator score × weight)</div>
            <div><span className="text-muted-foreground">Step 3:</span> Risk Score = 100 − Health Score</div>
            <div className="mt-2"><span className="text-muted-foreground">Risk bands:</span> Critical &lt; 30 health · High 30-49 · Moderate 50-69 · Low ≥ 70</div>
          </div>

          {/* Weight breakdown chart */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold">Indicator contribution to score</p>
              <p className="text-[10px] text-muted-foreground">Health points earned (green) + Risk points lost (red) = 100</p>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 36)}>
              <BarChart data={chartData} layout="vertical" stackOffset="expand">
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#999' }} width={110} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => `${v} pts`} />
                <Legend wrapperStyle={{ fontSize: '11px' }} iconType="square" />
                <Bar dataKey="Health points" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} activeBar={false} />
                <Bar dataKey="Risk points (lost)" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} activeBar={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown table */}
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Indicator</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Value</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Score (/100)</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Weight</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Weighted score</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b, i) => {
                  const style = SEVERITY_STYLES[b.severity];
                  return (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{b.name}</td>
                      <td className="px-3 py-2 text-right font-mono">{b.value}</td>
                      <td className="px-3 py-2 text-right font-mono">{b.score}</td>
                      <td className="px-3 py-2 text-right font-mono text-muted-foreground">{b.weightPct}%</td>
                      <td className="px-3 py-2 text-right font-mono">{b.healthContribution}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={cn('text-[9px]', style.text)}>{style.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-muted/30 font-semibold">
                  <td className="px-3 py-2">Totals</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right font-mono">100%</td>
                  <td className="px-3 py-2 text-right font-mono">{healthScore}</td>
                  <td className="px-3 py-2 text-[10px] text-muted-foreground">Health Score</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detailed per-indicator analysis with mitigations */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4" />
            Per-Indicator Analysis & Mitigation
          </CardTitle>
          <CardDescription className="text-xs">
            Each indicator below shows the formula, what the current value means, why it matters, and specific actions to improve it. Sorted by biggest risk contribution first.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {breakdown.map((b, i) => {
            const style = SEVERITY_STYLES[b.severity];
            return (
              <div key={i} className={cn('p-4 rounded-md border', style.bg, style.border)}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className={cn('h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold', style.bg, style.text)}>
                      {b.score}
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{b.name}</p>
                      <p className="text-[11px] text-muted-foreground">{b.detail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn('text-[9px]', style.text)}>{style.label}</Badge>
                    <Badge variant="outline" className="text-[9px]">Weight {b.weightPct}%</Badge>
                    <Badge variant="outline" className="text-[9px] text-red-600">
                      {b.riskContribution} pts lost
                    </Badge>
                  </div>
                </div>

                {b.meta && (
                  <div className="space-y-2 mt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-2 rounded bg-background/60">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-0.5">Formula</p>
                        <p className="text-xs font-mono">{b.meta.formula}</p>
                      </div>
                      <div className="p-2 rounded bg-background/60">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-0.5">Scoring thresholds</p>
                        <ul className="text-[11px] space-y-0.5">
                          {b.meta.thresholds.map((t, j) => (
                            <li key={j} className={cn('flex items-center gap-2', b.score === t.score && 'font-semibold')}>
                              <span className="font-mono text-[10px] text-muted-foreground w-8">{t.score}</span>
                              <span>{t.range}</span>
                              {b.score === t.score && <span className="text-[9px] text-primary">← current</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="p-2 rounded bg-background/60">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-0.5">Why it matters</p>
                      <p className="text-xs leading-relaxed">{b.meta.whyItMatters}</p>
                    </div>

                    {b.severity !== 'healthy' && (
                      <div className="p-2 rounded bg-background/60">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">How to mitigate this</p>
                        <ul className="text-xs space-y-1 leading-relaxed">
                          {b.meta.mitigations.map((m, j) => (
                            <li key={j} className="flex items-start gap-2">
                              <CheckCircle2 className="h-3 w-3 text-green-600 mt-0.5 shrink-0" />
                              <span>{m}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Step-by-step Action Plan */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Overall Action Plan
          </CardTitle>
          <CardDescription className="text-xs">
            Broader next steps ordered by urgency. Applies to <span className="font-medium capitalize">{riskLevelKey}</span> risk clients.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3">
            {actionPlan.map((step, i) => {
              const Icon = step.icon;
              const urgency = URGENCY_LABELS[step.urgency];
              return (
                <li key={i} className="flex gap-3 p-3 rounded-md border bg-card">
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold">{step.title}</span>
                      <Badge className={cn('text-[9px] px-1.5', urgency.color)} variant="outline">
                        {urgency.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      {/* Recommended RoniDoc Tools */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Run these in RoniDoc
          </CardTitle>
          <CardDescription className="text-xs">Click to jump straight to the tool</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {relevantTools.map(tool => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.href}
                  onClick={() => navigate(tool.href)}
                  className="flex items-start gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors text-left"
                >
                  <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{tool.title}</p>
                    <p className="text-[11px] text-muted-foreground">{tool.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ATO / Regulatory References */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Regulatory References & Citations
          </CardTitle>
          <CardDescription className="text-xs">Official ATO, ASIC and legislative sources relevant to this risk level</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {relevantRefs.map((ref, i) => {
              const Icon = ref.icon;
              return (
                <a
                  key={i}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-3 p-3 rounded-md border hover:bg-muted/40 transition-colors"
                >
                  <div className="h-8 w-8 rounded-md bg-muted text-foreground flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold">{ref.title}</span>
                      <Badge variant="outline" className="text-[9px]">{ref.source}</Badge>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{ref.description}</p>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="mt-4 p-3 rounded-md bg-amber-500/5 border border-amber-500/20 text-[11px] text-muted-foreground leading-relaxed">
            <strong className="text-amber-600">Important:</strong> This action plan is decision-support guidance only and does not constitute legal, insolvency or tax advice.
            For critical or high-risk situations, directors should engage a registered insolvency practitioner, tax lawyer and/or their accountant urgently.
            Links above point to official sources as of the current date; ATO/ASIC rules change - always verify the latest requirements.
          </div>
        </CardContent>
      </Card>

      {/* Quick link to client file */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => navigate(`/panel/files/clients/${client.clientId}`)} className="gap-2">
          <Users className="h-4 w-4" /> Open client file
        </Button>
      </div>
    </div>
  );
}
